import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import {
  Lock as LockIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import API_CONFIG from '../config/api';

const ForcePasswordChangeDialog = ({
  open,
  onPasswordChanged,
  onClose,
  title = 'Change Your Password',
  description = 'Please choose a strong password.',
  requireChange = true,
  currentPasswordLabel = 'Current Password',
}) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState(0);

  useEffect(() => {
    if (!open) {
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setError(null);
      setPasswordStrength(0);
    }
  }, [open]);

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 15;
    if (/[a-z]/.test(password)) strength += 15;
    if (/[A-Z]/.test(password)) strength += 15;
    if (/\d/.test(password)) strength += 15;
    if (/[!@#$%^&*]/.test(password)) strength += 15;
    return strength;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
    
    if (field === 'newPassword') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
  };

  const validatePassword = () => {
    if (formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (formData.newPassword === formData.currentPassword) {
      setError('New password must be different from current password');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('stratovai_access_token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/users/me/password`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: formData.currentPassword,
          new_password: formData.newPassword
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to change password');
      }
      
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordStrength(0);
      onPasswordChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStrengthColor = () => {
    if (passwordStrength < 40) return 'error';
    if (passwordStrength < 70) return 'warning';
    return 'success';
  };

  const getStrengthLabel = () => {
    if (passwordStrength < 40) return 'Weak';
    if (passwordStrength < 70) return 'Medium';
    return 'Strong';
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={requireChange}
      onClose={requireChange ? undefined : onClose}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockIcon color={requireChange ? 'warning' : 'primary'} />
          <Typography variant="h6">{title}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity={requireChange ? 'warning' : 'info'} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {requireChange ? 'Password Change Required' : 'Password Update'}
          </Typography>
          <Typography variant="body2">
            {description}
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            type="password"
            label={currentPasswordLabel}
            value={formData.currentPassword}
            onChange={(e) => handleChange('currentPassword', e.target.value)}
            required
            autoFocus
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            type="password"
            label="New Password"
            value={formData.newPassword}
            onChange={(e) => handleChange('newPassword', e.target.value)}
            required
            sx={{ mb: 1 }}
            helperText="Minimum 8 characters, include uppercase, lowercase, numbers, and symbols"
          />

          {formData.newPassword && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Password Strength
                </Typography>
                <Typography variant="caption" color={getStrengthColor()}>
                  {getStrengthLabel()}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={passwordStrength}
                color={getStrengthColor()}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          )}

          <TextField
            fullWidth
            type="password"
            label="Confirm New Password"
            value={formData.confirmPassword}
            onChange={(e) => handleChange('confirmPassword', e.target.value)}
            required
            sx={{ mb: 2 }}
            error={!!formData.confirmPassword && formData.newPassword !== formData.confirmPassword}
            helperText={
              !!formData.confirmPassword && formData.newPassword !== formData.confirmPassword
                ? 'Passwords do not match'
                : ''
            }
          />

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            {!requireChange && (
              <Button
                fullWidth
                variant="outlined"
                onClick={onClose}
                startIcon={<CloseIcon />}
              >
                Cancel
              </Button>
            )}
            {requireChange && (
              <Button
                fullWidth
                variant="outlined"
                disabled
                startIcon={<CloseIcon />}
              >
                Cancel (Disabled)
              </Button>
            )}
            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={loading || !formData.currentPassword || !formData.newPassword || !formData.confirmPassword}
              startIcon={loading ? <CircularProgress size={16} /> : <CheckIcon />}
            >
              {loading ? 'Changing...' : 'Change Password'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ForcePasswordChangeDialog;

